import { forwardRef, KeyboardEvent, MouseEvent } from 'react';
import clsx from 'clsx';
import { IconButton, makeStyles, Theme, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DateRangeIcon from '@material-ui/icons/DateRange';
import DescriptionIcon from '@material-ui/icons/Description';
import PersonIcon from '@material-ui/icons/PersonOutline';
import ReceiptIcon from '@material-ui/icons/Receipt';
import RefreshIcon from '@material-ui/icons/Refresh';
import ViewListIcon from '@material-ui/icons/ViewList';
import WorkIcon from '@material-ui/icons/WorkOutline';

import { TabItem } from 'contexts/TabContext';
import { TabRecordType, TabSection, TabType } from 'constants/tabs';

/** Contextual icon per tab type (TAB-02). */
const TAB_ICON: Record<TabType, typeof ViewListIcon> = {
  LIST: ViewListIcon,
  [TabRecordType.QUOTATION]: DescriptionIcon,
  [TabRecordType.CLIENT]: PersonIcon,
  [TabRecordType.JOB]: WorkIcon,
  [TabRecordType.INVOICE]: ReceiptIcon
};

/** Pinned tabs show the section they stand for; Schedule is a calendar, not a list. */
const PINNED_ICON: Partial<Record<TabSection, typeof ViewListIcon>> = {
  [TabSection.SCHEDULE]: DateRangeIcon
};

const resolveIcon = (tab: TabItem) => {
  if (tab.pinned) {
    return PINNED_ICON[tab.recordId as TabSection] || ViewListIcon;
  }

  return TAB_ICON[tab.type];
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: 120,
    maxWidth: 200,
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(0, 1, 0, 1.25),
    border: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    fontSize: 13,
    textAlign: 'left',
    outline: 'none',
    '&:hover': {
      backgroundColor: '#EDF2F5',
      color: theme.palette.text.primary
    },
    '&:focus-visible': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`
    }
  },
  /** Pinned list tab hugs its label and never competes for width with record tabs (TAB-01). */
  pinned: {
    flex: '0 0 auto',
    minWidth: 0,
    maxWidth: 'none',
    paddingRight: theme.spacing(1.5),
    fontWeight: 500
  },
  active: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    borderBottomColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  /** TAB-05: a brief highlight says "this tab was already open" instead of opening a duplicate. */
  flashing: {
    animation: '$tabFlash 500ms ease 2'
  },
  '@keyframes tabFlash': {
    '0%, 100%': { backgroundColor: 'transparent' },
    '35%': { backgroundColor: '#EAF4F8' }
  },
  icon: {
    fontSize: 16,
    flex: 'none',
    marginRight: theme.spacing(0.75),
    color: theme.palette.text.hint
  },
  iconActive: {
    color: theme.palette.primary.main
  },
  label: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  closeButton: {
    flex: 'none',
    padding: 2,
    marginLeft: theme.spacing(0.5),
    opacity: 0,
    '$root:hover &': {
      opacity: 1
    }
  },
  /** The active tab always shows its close button; the rest reveal it on hover (TAB-04). */
  closeButtonVisible: {
    opacity: 1
  },
  closeIcon: {
    fontSize: 14
  },
  refreshButton: {
    flex: 'none',
    padding: 2,
    marginLeft: theme.spacing(0.5)
  },
  /** Holds its slot on the pinned tab, whose measured width decides what else fits in the bar. */
  refreshButtonHidden: {
    visibility: 'hidden'
  }
}));

interface Props {
  tab: TabItem;
  isActive: boolean;
  isFlashing: boolean;
  onActivate: (key: string) => void;
  onClose: (key: string) => void;
  onRefresh: (key: string) => void;
}

/** Forwards a ref so the bar can measure the pinned tab when deciding what still fits. */
const TabButton = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { tab, isActive, isFlashing, onActivate, onClose, onRefresh } = props;
  const classes = useStyles();

  const Icon = resolveIcon(tab);

  const handleActivate = () => {
    if (!isActive) {
      onActivate(tab.key);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClose(tab.key);
  };

  const handleRefresh = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onRefresh(tab.key);
  };

  return (
    <div
      ref={ref}
      role='tab'
      tabIndex={0}
      aria-selected={isActive}
      title={tab.label}
      className={clsx(classes.root, { [classes.pinned]: tab.pinned, [classes.active]: isActive, [classes.flashing]: isFlashing })}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      <Icon className={clsx(classes.icon, { [classes.iconActive]: isActive })} />
      <span className={classes.label}>{tab.label}</span>
      {/* Only the tab on screen reloads — reloading a hidden one would look like nothing happened. */}
      {(isActive || tab.pinned) && (
        <Tooltip title={tab.pinned ? 'Reload' : 'Reload discards unsaved changes'}>
          <IconButton
            size='small'
            aria-label={`Reload ${tab.label}`}
            className={clsx(classes.refreshButton, { [classes.refreshButtonHidden]: !isActive })}
            onClick={handleRefresh}
          >
            <RefreshIcon className={classes.closeIcon} />
          </IconButton>
        </Tooltip>
      )}
      {!tab.pinned && (
        <Tooltip title='Closing discards unsaved changes'>
          <IconButton
            size='small'
            aria-label={`Close ${tab.label}`}
            className={clsx(classes.closeButton, { [classes.closeButtonVisible]: isActive })}
            onClick={handleClose}
          >
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
});

TabButton.displayName = 'TabButton';

export default TabButton;
